import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaDownload } from '@react-icons/all-files/fa/FaDownload';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaExternalLinkAlt } from '@react-icons/all-files/fa/FaExternalLinkAlt';
import { FaFileAlt } from '@react-icons/all-files/fa/FaFileAlt';
import { FaFileImage } from '@react-icons/all-files/fa/FaFileImage';
import { FaFilePdf } from '@react-icons/all-files/fa/FaFilePdf';
import { FaRegFileAlt } from '@react-icons/all-files/fa/FaRegFileAlt';
import { FaRegFileImage } from '@react-icons/all-files/fa/FaRegFileImage';
import { FaRegFilePdf } from '@react-icons/all-files/fa/FaRegFilePdf';
import { FaFolder } from '@react-icons/all-files/fa/FaFolder';
import { FaFolderPlus } from '@react-icons/all-files/fa/FaFolderPlus';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { FaUpload } from '@react-icons/all-files/fa/FaUpload';
import Confirm, { useConfirm } from '../commons/Confirm';
import {
  createProfaseArchivoFolder,
  deleteProfaseArchivo,
  downloadProfaseArchivoContenido,
  joinProfaseArchivoPath,
  openProfaseArchivoContenido,
  uploadProfaseArchivoToPath,
  type IProfaseArchivo,
} from './profase-archivo-api';
import { canPreviewArchivo, isImageFile, isPdfFile, isTxtFile } from './archivo-file-types';
import ImageViewerModal from './ImageViewerModal';
import PdfViewerModal from './PdfViewerModal';
import TxtViewerModal from './TxtViewerModal';
import './file-explorer.css';

interface BreadcrumbItem {
  label: string;
  path: string;
}

function sortArchivos(items: IProfaseArchivo[]): IProfaseArchivo[] {
  return [...items].sort((a, b) => {
    if (a.esDirectorio !== b.esDirectorio) {
      return a.esDirectorio ? -1 : 1;
    }
    return a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' });
  });
}

function decodeExplorerPath(splat: string | undefined): string {
  if (!splat) {
    return '';
  }
  return splat.split('/').filter(Boolean).map(decodeURIComponent).join('/');
}

function encodeExplorerPath(logicalPath: string): string {
  if (!logicalPath) {
    return '';
  }
  return logicalPath.split('/').map(encodeURIComponent).join('/');
}

function explorerUrl(logicalPath: string): string {
  const encoded = encodeExplorerPath(logicalPath);
  return encoded ? `/explorer/${encoded}` : '/explorer';
}

function breadcrumbsFromLogicalPath(logicalPath: string): BreadcrumbItem[] {
  if (!logicalPath) {
    return [];
  }
  const parts = logicalPath.split('/');
  return parts.map((part, index) => ({
    label: part,
    path: parts.slice(0, index + 1).join('/'),
  }));
}

async function fetchHijos(logicalPath: string): Promise<IProfaseArchivo[]> {
  const searchParams = new URLSearchParams();
  searchParams.append('path', logicalPath || '/');
  searchParams.append('page', '0');
  searchParams.append('size', '500');
  searchParams.append('sort', 'nombre,asc');
  searchParams.append('cacheBuster', String(Date.now()));

  const { data } = await axios.get<IProfaseArchivo[]>(`api/profase-archivos/hijos?${searchParams.toString()}`);
  return sortArchivos(data);
}

function canDownloadArchivo(archivo: IProfaseArchivo | null): archivo is IProfaseArchivo {
  return archivo != null
    && !archivo.esDirectorio
    && archivo.id != null
    && Boolean(archivo.contenidoUuid);
}

function canDeleteArchivo(archivo: IProfaseArchivo | null): archivo is IProfaseArchivo {
  return archivo != null && archivo.id != null;
}

const FileExplorer: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const logicalPath = decodeExplorerPath(params['*']);
  const breadcrumbs = useMemo(() => breadcrumbsFromLogicalPath(logicalPath), [logicalPath]);

  const [currentPath, setCurrentPath] = useState<IProfaseArchivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showCreateFolderForm, setShowCreateFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [previewArchivo, setPreviewArchivo] = useState<IProfaseArchivo | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const newFolderInputRef = useRef<HTMLInputElement>(null);
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const selectedItem = useMemo(
    () => currentPath.find(node => node.path === selectedPath) ?? null,
    [currentPath, selectedPath],
  );

  const downloadEnabled = canDownloadArchivo(selectedItem);
  const previewEnabled = canPreviewArchivo(selectedItem, canDownloadArchivo);
  const deleteEnabled = canDeleteArchivo(selectedItem);
  const fileActionBusy = opening || downloading || uploading || creatingFolder || deleting;
  const uploadEnabled = !loading && !error;

  const uploadDestinationPath = useMemo(() => {
    if (selectedItem?.esDirectorio) {
      return selectedItem.path;
    }
    return logicalPath;
  }, [selectedItem, logicalPath]);

  const listingParentId = useMemo(() => {
    if (!logicalPath || currentPath.length === 0) {
      return undefined;
    }
    return currentPath[0].archivoPadre?.id;
  }, [logicalPath, currentPath]);

  const loadByPath = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    try {
      const items = await fetchHijos(path);
      setCurrentPath(items);
    } catch {
      setError(path ? 'No se encontró la carpeta solicitada.' : 'No se pudieron cargar los archivos.');
      setCurrentPath([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedPath(null);
    setDownloadError(null);
    setOpening(false);
    setDownloading(false);
    setUploading(false);
    setCreatingFolder(false);
    setShowCreateFolderForm(false);
    setNewFolderName('');
    setDeleting(false);
    setPreviewArchivo(null);
    loadByPath(logicalPath);
  }, [logicalPath, loadByPath]);

  useEffect(() => {
    setConfirmLoading(deleting);
  }, [deleting, setConfirmLoading]);

  const openFolder = (folder: IProfaseArchivo) => {
    if (!folder.esDirectorio) {
      return;
    }
    navigate(explorerUrl(folder.path));
  };

  const goToPath = (path: string) => {
    navigate(explorerUrl(path));
  };

  const goRoot = () => {
    navigate('/explorer');
  };

  const handleSelect = (node: IProfaseArchivo) => {
    setSelectedPath(node.path);
    setDownloadError(null);
  };

  const handleClearSelection = () => {
    setSelectedPath(null);
    setDownloadError(null);
  };

  const handlePreview = () => {
    if (!previewEnabled || fileActionBusy) {
      return;
    }
    setPreviewArchivo(selectedItem);
    setDownloadError(null);
  };

  const handleOpen = async () => {
    if (!downloadEnabled || fileActionBusy) {
      return;
    }

    setOpening(true);
    setDownloadError(null);
    try {
      await openProfaseArchivoContenido(selectedItem.id!, selectedItem.nombre);
    } catch {
      setDownloadError('No se pudo abrir el archivo.');
    } finally {
      setOpening(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadEnabled || fileActionBusy) {
      return;
    }

    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadProfaseArchivoContenido(selectedItem);
    } catch {
      setDownloadError('No se pudo descargar el archivo.');
    } finally {
      setDownloading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteEnabled || fileActionBusy) {
      return;
    }

    setDeleting(true);
    setDownloadError(null);
    try {
      await deleteProfaseArchivo(selectedItem.id!);
      dismiss();
      setSelectedPath(null);
      await loadByPath(logicalPath);
    } catch {
      setDownloadError(
        selectedItem.esDirectorio
          ? 'No se pudo eliminar la carpeta. Debe estar vacía.'
          : 'No se pudo eliminar el archivo.',
      );
      dismiss();
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = () => {
    if (!deleteEnabled || fileActionBusy) {
      return;
    }

    const message = selectedItem.esDirectorio
      ? (
        <>
          ¿Confirmas que deseas eliminar la carpeta <strong>{selectedItem.nombre}</strong>?
          Debe estar vacía. Esta acción no se puede deshacer.
        </>
      )
      : (
        <>
          ¿Confirmas que deseas eliminar <strong>{selectedItem.nombre}</strong>?
          Esta acción no se puede deshacer.
        </>
      );

    ask(
      message,
      () => void confirmDelete(),
      { title: 'Eliminar', confirmLabel: 'Eliminar', variant: 'danger' },
    );
  };

  const handleUploadClick = () => {
    if (!uploadEnabled || fileActionBusy) {
      return;
    }
    setShowCreateFolderForm(false);
    setNewFolderName('');
    fileInputRef.current?.click();
  };

  const handleUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !uploadEnabled || fileActionBusy) {
      return;
    }

    const targetPath = uploadDestinationPath;
    const filePath = joinProfaseArchivoPath(targetPath, file.name);
    if (currentPath.some(item => item.path === filePath)) {
      setDownloadError('Ya existe un archivo con ese nombre en esta carpeta.');
      return;
    }

    setUploading(true);
    setDownloadError(null);
    try {
      await uploadProfaseArchivoToPath(targetPath, file, {
        listingParentId: targetPath === logicalPath ? listingParentId : undefined,
        selectedFolder: selectedItem?.esDirectorio ? selectedItem : null,
      });
      await loadByPath(logicalPath);
    } catch {
      setDownloadError('No se pudo subir el archivo.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolderClick = () => {
    if (!uploadEnabled || fileActionBusy) {
      return;
    }
    setShowCreateFolderForm(prev => {
      const next = !prev;
      if (next) {
        setDownloadError(null);
        setTimeout(() => newFolderInputRef.current?.focus(), 0);
      } else {
        setNewFolderName('');
      }
      return next;
    });
  };

  const handleCreateFolderSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!uploadEnabled || fileActionBusy || !showCreateFolderForm) {
      return;
    }

    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      setDownloadError('Indique el nombre de la carpeta.');
      return;
    }
    if (trimmedName.includes('/')) {
      setDownloadError('El nombre de la carpeta no puede contener "/".');
      return;
    }

    const targetPath = uploadDestinationPath;
    const folderPath = joinProfaseArchivoPath(targetPath, trimmedName);
    if (currentPath.some(item => item.path === folderPath)) {
      setDownloadError('Ya existe una carpeta o archivo con ese nombre.');
      return;
    }

    setCreatingFolder(true);
    setDownloadError(null);
    try {
      await createProfaseArchivoFolder(targetPath, trimmedName, {
        listingParentId: targetPath === logicalPath ? listingParentId : undefined,
        selectedFolder: selectedItem?.esDirectorio ? selectedItem : null,
      });
      setShowCreateFolderForm(false);
      setNewFolderName('');
      await loadByPath(logicalPath);
    } catch {
      setDownloadError('No se pudo crear la carpeta.');
    } finally {
      setCreatingFolder(false);
    }
  };

  return (
    <div className="file-explorer">
      {confirmConfig && (
        <div style={{ marginBottom: '1rem' }}>
          <Confirm config={confirmConfig} />
        </div>
      )}
      <div className="file-explorer__toolbar">
        <input
          ref={fileInputRef}
          type="file"
          className="file-explorer__file-input"
          onChange={handleUploadFileChange}
        />
        <button
          type="button"
          className="file-explorer__toolbar-btn file-explorer__toolbar-btn--icon"
          disabled={!uploadEnabled || fileActionBusy}
          onClick={handleUploadClick}
          title="Subir archivo a la carpeta actual"
          aria-label="Subir archivo a la carpeta actual"
        >
          {uploading ? (
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <FaUpload />
          )}
        </button>
        <button
          type="button"
          className="file-explorer__toolbar-btn file-explorer__toolbar-btn--icon"
          disabled={!uploadEnabled || fileActionBusy}
          onClick={handleCreateFolderClick}
          title="Crear carpeta"
          aria-label="Crear carpeta"
          aria-pressed={showCreateFolderForm}
        >
          {creatingFolder ? (
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <FaFolderPlus />
          )}
        </button>
        <button
          type="button"
          className="file-explorer__toolbar-btn file-explorer__toolbar-btn--danger file-explorer__toolbar-btn--icon"
          disabled={!deleteEnabled || fileActionBusy}
          onClick={handleDelete}
          title="Eliminar elemento seleccionado"
          aria-label="Eliminar elemento seleccionado"
        >
          {deleting ? (
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <FaTrash />
          )}
        </button>
        <div className="file-explorer__toolbar-actions">
          <button
            type="button"
            className="file-explorer__toolbar-btn file-explorer__toolbar-btn--icon"
            disabled={!previewEnabled || fileActionBusy}
            onClick={handlePreview}
            title="Ver archivo seleccionado"
            aria-label="Ver archivo seleccionado"
          >
            <FaEye />
          </button>
          <button
            type="button"
            className="file-explorer__toolbar-btn"
            disabled={!downloadEnabled || fileActionBusy}
            onClick={handleOpen}
            title="Abrir archivo seleccionado"
          >
            {opening ? (
              <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <FaExternalLinkAlt />
            )}
            Abrir
          </button>
          <button
            type="button"
            className="file-explorer__toolbar-btn"
            disabled={!downloadEnabled || fileActionBusy}
            onClick={handleDownload}
            title="Descargar archivo seleccionado"
          >
            {downloading ? (
              <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <FaDownload />
            )}
            Descargar
          </button>
        </div>
        {downloadError && (
          <div className="file-explorer__toolbar-message">{downloadError}</div>
        )}
      </div>

      <div className="file-explorer__pane" onClick={handleClearSelection}>
        <div className="file-explorer__breadcrumbs">
          <span className="file-explorer__breadcrumb-link" onClick={goRoot}>
            Profase
          </span>
          {breadcrumbs.map(crumb => (
            <span key={crumb.path} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span className="file-explorer__breadcrumb-separator">›</span>
              <span
                className="file-explorer__breadcrumb-link"
                onClick={() => goToPath(crumb.path)}
              >
                {crumb.label}
              </span>
            </span>
          ))}
        </div>

        <div className="file-explorer__content">
          {loading && (
            <div className="file-explorer__status">
              <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
              Cargando…
            </div>
          )}

          {!loading && error && (
            <div className="file-explorer__error">{error}</div>
          )}

          {!loading && !error && currentPath.length === 0 && (
            <div className="file-explorer__empty">Esta carpeta está vacía.</div>
          )}

          {!loading && !error && currentPath.length > 0 && (
            <div className="file-explorer__grid">
              {currentPath.map(node => {
                const isSelected = selectedPath === node.path;
                return (
                  <div
                    key={node.id ?? node.path}
                    className={`file-explorer__item${isSelected ? ' file-explorer__item--selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(node);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      openFolder(node);
                    }}
                  >
                    <div className="file-explorer__item-icon">
                      {node.esDirectorio ? (
                        <FaFolder color="#f0ad4e" />
                      ) : isPdfFile(node.nombre) ? (
                        <span className="file-explorer__item-icon-pdf">
                          <FaFilePdf className="file-explorer__item-icon-pdf__fill" aria-hidden />
                          <FaRegFilePdf className="file-explorer__item-icon-pdf__lines" aria-hidden />
                        </span>
                      ) : isTxtFile(node.nombre) ? (
                        <span className="file-explorer__item-icon-txt">
                          <FaFileAlt className="file-explorer__item-icon-txt__fill" aria-hidden />
                          <FaRegFileAlt className="file-explorer__item-icon-txt__lines" aria-hidden />
                        </span>
                      ) : isImageFile(node.nombre) ? (
                        <span className="file-explorer__item-icon-image">
                          <FaFileImage className="file-explorer__item-icon-image__fill" aria-hidden />
                          <FaRegFileImage className="file-explorer__item-icon-image__lines" aria-hidden />
                        </span>
                      ) : (
                        <FaFileAlt color="#5bc0de" />
                      )}
                    </div>
                    <div className="file-explorer__item-name">{node.nombre}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCreateFolderForm && (
        <div className="file-explorer__bottom-bar">
          <form
            className="file-explorer__create-folder"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreateFolderSubmit}
          >
            <input
              ref={newFolderInputRef}
              type="text"
              className="file-explorer__create-folder-input"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nombre de la carpeta"
              disabled={creatingFolder}
              maxLength={255}
              aria-label="Nombre de la carpeta"
            />
            <button
              type="submit"
              className="file-explorer__toolbar-btn"
              disabled={creatingFolder || !newFolderName.trim()}
            >
              {creatingFolder ? (
                <FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} />
              ) : null}
              Crear Carpeta
            </button>
          </form>
        </div>
      )}
      <PdfViewerModal
        open={previewArchivo != null && isPdfFile(previewArchivo.nombre)}
        archivo={previewArchivo}
        onClose={() => setPreviewArchivo(null)}
      />
      <TxtViewerModal
        open={previewArchivo != null && isTxtFile(previewArchivo.nombre)}
        archivo={previewArchivo}
        onClose={() => setPreviewArchivo(null)}
      />
      <ImageViewerModal
        open={previewArchivo != null && isImageFile(previewArchivo.nombre)}
        archivo={previewArchivo}
        onClose={() => setPreviewArchivo(null)}
      />
    </div>
  );
};

export default FileExplorer;
